import { useEffect, useState } from 'react'
import RosterScanModal from './RosterScanModal.jsx'

// 3-step welcome wizard for a brand-new manager.
//
//   Step 1: Restaurant info — confirm/edit name + timezone (pre-filled
//           from sign-up metadata)
//   Step 2: Daily targets — daily waste $ + large-entry alert $
//   Step 3: Wrap-up — pointers to Settings → Items → Import POS, the
//           Recipe Builder, and the Stocktake tab
//
// The wizard is fully skippable. "Skip the rest" persists what's been
// entered so far, marks the restaurant onboarded=true, and dismisses
// the welcome card. The manager can refine everything later from
// Settings.
const TIMEZONE_OPTIONS = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
]

export default function OnboardingWizard({
  open,
  onClose,
  restaurant,
  onUpdateBasics,    // (patch) => Promise — persists to restaurants
  onMarkOnboarded,   // () => Promise
}) {
  const [step, setStep]           = useState(1)
  const [form, setForm]           = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)
  const [scanOpen, setScanOpen]   = useState(false)
  const [scanToast, setScanToast] = useState('')

  useEffect(() => {
    if (!open) return
    setStep(1)
    setError(null)
    setSubmitting(false)
    setForm({
      name: restaurant?.name ?? '',
      timezone: restaurant?.timezone ?? 'America/Chicago',
      daily_target: String(restaurant?.daily_target ?? '50'),
      large_entry_threshold: String(restaurant?.large_entry_threshold ?? '50'),
    })
  }, [open, restaurant?.id])  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !form) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function persistStep1() {
    if (!form.name.trim()) {
      setError('Give your kitchen a name.')
      return false
    }
    setError(null)
    setSubmitting(true)
    try {
      await onUpdateBasics({
        name: form.name.trim(),
        timezone: form.timezone,
      })
      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OnboardingWizard] step 1 failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
      return false
    } finally {
      setSubmitting(false)
    }
  }

  async function persistStep2() {
    const dt = Number(form.daily_target)
    const le = Number(form.large_entry_threshold)
    if (!(dt > 0)) {
      setError('Daily waste target must be greater than 0.')
      return false
    }
    if (!(le > 0)) {
      setError('Large-entry threshold must be greater than 0.')
      return false
    }
    setError(null)
    setSubmitting(true)
    try {
      await onUpdateBasics({
        daily_target: dt,
        large_entry_threshold: le,
      })
      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OnboardingWizard] step 2 failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
      return false
    } finally {
      setSubmitting(false)
    }
  }

  async function handleNext() {
    if (step === 1) {
      const ok = await persistStep1()
      if (ok) setStep(2)
    } else if (step === 2) {
      const ok = await persistStep2()
      if (ok) setStep(3)
    } else if (step === 3) {
      // Final — mark onboarded and close.
      setSubmitting(true)
      try {
        await onMarkOnboarded()
        onClose?.()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[OnboardingWizard] mark onboarded failed', e)
        setError(`Could not finish: ${e?.message || 'try again.'}`)
      } finally {
        setSubmitting(false)
      }
    }
  }

  async function handleSkipRest() {
    // Persist current step's input if it's valid, then mark onboarded.
    setSubmitting(true)
    try {
      if (step === 1 && form.name.trim()) {
        await onUpdateBasics({
          name: form.name.trim(),
          timezone: form.timezone,
        })
      } else if (step === 2 && Number(form.daily_target) > 0) {
        await onUpdateBasics({
          daily_target: Number(form.daily_target),
          large_entry_threshold: Number(form.large_entry_threshold),
        })
      }
      await onMarkOnboarded()
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OnboardingWizard] skip failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Welcome wizard"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h2 className="text-xl font-medium">Welcome — let's set up your kitchen</h2>
              <div className="mt-0.5 text-xs text-black/55">Step {step} of 3</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>

          {/* Progress bar */}
          <div className="mx-4 mb-3 h-1.5 rounded-full bg-black/10">
            <div
              className="h-1.5 rounded-full bg-blue-600 transition-all"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>

          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {step === 1 && (
            <>
              <p className="text-sm leading-relaxed text-black/70">
                Confirm your kitchen's name and time zone. We'll use the time
                zone for daily-rollover boundaries and timestamps.
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="onb-name">
                  Restaurant name
                </label>
                <input
                  id="onb-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="onb-tz">
                  Time zone
                </label>
                <select
                  id="onb-tz"
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                  className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-black/55">
                  Pre-filled from your browser. Change if you don't see your zone listed.
                </p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm leading-relaxed text-black/70">
                Two targets that drive the alerts on your Huddle.
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="onb-daily">
                  Daily waste target ($)
                </label>
                <input
                  id="onb-daily"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  value={form.daily_target}
                  onChange={(e) => set('daily_target', e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/55">
                  When today's waste passes this, the Huddle waste card turns red.
                  $50 is a reasonable starting target for a small kitchen.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="onb-large">
                  Large-entry alert ($)
                </label>
                <input
                  id="onb-large"
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min="0"
                  value={form.large_entry_threshold}
                  onChange={(e) => set('large_entry_threshold', e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/55">
                  A single waste log above this value triggers the
                  "owner notified" banner so you don't miss big losses.
                </p>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm leading-relaxed text-black/70">
                You're set. From here, you'll get the most value once you:
              </p>
              <ul className="space-y-3 text-sm leading-snug text-black/85">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span>
                    <strong>Connect your POS.</strong>{' '}
                    Settings → Items & Pars → "Import POS" — connect Square or
                    Toast, or upload an item sales CSV, to seed your catalog
                    and sales history.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span>
                    <strong>Build a few recipes.</strong>{' '}
                    Settings → Recipes → "+ AI Build Recipe" — describe a
                    dish, the AI picks ingredients from your catalog and
                    suggests a menu price.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span>
                    <strong>Run a stocktake.</strong>{' '}
                    Stocktake tab → "+ Start new count" — physical counts
                    set the baseline for the variance report and keep on-hand
                    accurate.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                  <span>
                    <strong>Generate your first brief.</strong>{' '}
                    Open the Huddle and tap Generate — you'll get a daily AI
                    brief alongside today's roster, prime cost, and waste
                    target, all designed for a 30-second read.
                  </span>
                </li>
              </ul>
              <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                <div className="text-sm font-medium text-blue-900">
                  Already have a schedule on paper?
                </div>
                <p className="mt-1 text-xs leading-relaxed text-blue-900/80">
                  Snap a photo of your printed schedule or staff list and we'll
                  pull out the staff and shifts. You review and edit everything
                  before it's saved.
                </p>
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="mt-3 min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
                >
                  Import your schedule from a photo
                </button>
                {scanToast && (
                  <p className="mt-2 text-xs font-medium text-emerald-700">{scanToast}</p>
                )}
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 text-xs leading-relaxed text-blue-900">
                Everything you set up here can be changed later from
                Settings — including restaurant name, time zone, and the
                daily targets.
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleNext}
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Saving\u2026'
              : step === 1 ? 'Save and continue'
              : step === 2 ? 'Save and continue'
              : 'Done'}
          </button>
          {step < 3 && (
            <button
              type="button"
              onClick={handleSkipRest}
              disabled={submitting}
              className="mt-2 block w-full text-center text-sm font-medium text-black/55 hover:text-black/80 disabled:opacity-50"
            >
              Skip the rest — I'll set up later
            </button>
          )}
        </div>
      </div>

      {/* Cold-start photo import — no existing staff yet, so every
          extracted name becomes a new staff row. */}
      <RosterScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        activeStaff={[]}
        kind="both"
        onImported={({ staffCreated, shiftsInserted, skippedDupe, skippedUnmatched, skippedNoEnd }) => {
          const parts = []
          if (staffCreated   > 0) parts.push(`${staffCreated} staff added`)
          if (shiftsInserted > 0) parts.push(`${shiftsInserted} ${shiftsInserted === 1 ? 'shift' : 'shifts'} added`)
          if (skippedDupe      > 0) parts.push(`${skippedDupe} duplicate${skippedDupe === 1 ? '' : 's'} skipped`)
          if (skippedUnmatched > 0) parts.push(`${skippedUnmatched} skipped — no staff match`)
          if (skippedNoEnd     > 0) parts.push(`${skippedNoEnd} skipped — add an end time`)
          setScanToast(parts.length > 0 ? `Imported: ${parts.join(' · ')}` : 'Nothing new to import.')
        }}
      />
    </div>
  )
}
